import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../apiConfig';
import './Leaderboard.css';
import { ChevronDown, ArrowRight } from 'lucide-react';

const Leaderboard = () => {
    const [season, setSeason] = useState('2025');
    const [division, setDivision] = useState('137680'); // Default
    const [divisions, setDivisions] = useState([]);
    const [statType, setStatType] = useState('Batters');
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Fetch divisions for filter
        axios.get(`${API_BASE_URL}/currentDivision`)
            .then(res => setDivisions(res.data))
            .catch(err => console.error("Error fetching divisions:", err));
    }, []);

    useEffect(() => {
        setLoading(true);
        axios.get(`${API_BASE_URL}/leaderboard`, {
            params: { division_id: division, season, type: statType }
        })
        .then(res => {
            setLeaderboardData(res.data);
            setLoading(false);
        })
        .catch(err => {
            console.error("Error fetching leaderboard:", err);
            setLoading(false);
        });
    }, [division, season, statType]);

    const topPlayer = leaderboardData[0] || null;

    if (loading && leaderboardData.length === 0) return <div className="loader" style={{margin: '100px auto'}}></div>;

    return (
        <div className="leaderboard-page">
            <div className="leaderboard-header">
                <div className="container">
                    <h1>LeaderBoard</h1>
                </div>
            </div>

            <div className="container leaderboard-content">
                <div className="leaderboard-filters">
                    <div className="filter-item">
                        <select value={season} onChange={(e) => setSeason(e.target.value)}>
                            <option value="2025">Season 2025</option>
                            <option value="2024">Season 2024</option>
                        </select>
                    </div>
                    <div className="filter-item">
                        <select value={division} onChange={(e) => setDivision(e.target.value)}>
                            {divisions.map((div, i) => (
                                <option key={i} value={div.PCID}>{div.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-item">
                        <select value={statType} onChange={(e) => setStatType(e.target.value)}>
                            <option value="Batters">Batters</option>
                            <option value="Bowlers">Bowlers</option>
                        </select>
                    </div>
                </div>

                {topPlayer && (
                    <div className="top-player-banner">
                        <div className="banner-rank">#1</div>
                        <div className="banner-photo">
                            <img src={topPlayer.Photo || 'https://via.placeholder.com/224x224?text=No+Photo'} alt={topPlayer.player_name} />
                        </div>
                        <div className="banner-info">
                            <div className="player-name">
                                <h2>{topPlayer.player_name || topPlayer.Forename + ' ' + topPlayer.Surname}</h2>
                                <p>{topPlayer.team_name}</p>
                            </div>
                            <div className="player-stats-grid">
                                {statType === 'Batters' ? (
                                    <>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.runs}</span><span className="stat-label">Runs</span></div>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.matches}</span><span className="stat-label">Matches</span></div>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.average}</span><span className="stat-label">Avg</span></div>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.strike_rate || '-'}</span><span className="stat-label">SR</span></div>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.high_score}</span><span className="stat-label">HS</span></div>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.hundreds}/{topPlayer.fifties}</span><span className="stat-label">100s/50s</span></div>
                                    </>
                                ) : (
                                    <>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.wickets}</span><span className="stat-label">Wkts</span></div>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.matches}</span><span className="stat-label">Matches</span></div>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.average}</span><span className="stat-label">Avg</span></div>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.economy}</span><span className="stat-label">Eco</span></div>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.best_bowling}</span><span className="stat-label">BBI</span></div>
                                        <div className="stat-box"><span className="stat-value">{topPlayer.five_wickets || '0'}</span><span className="stat-label">5W</span></div>
                                    </>
                                )}
                            </div>
                            <div className="banner-actions">
                                <a href={`/player-profile/${topPlayer.player_id}`} style={{color: 'white'}}>Player Profile <ArrowRight size={16} /></a>
                            </div>
                        </div>
                    </div>
                )}

                <div className="leaderboard-table-container">
                    <table className="leaderboard-table">
                        <thead>
                            {statType === 'Batters' ? (
                                <tr>
                                    <th>Rank</th>
                                    <th>Player</th>
                                    <th>Team</th>
                                    <th>Runs</th>
                                    <th>Matches</th>
                                    <th>100s</th>
                                    <th>50s</th>
                                    <th>Average</th>
                                </tr>
                            ) : (
                                <tr>
                                    <th>Rank</th>
                                    <th>Player</th>
                                    <th>Team</th>
                                    <th>Wickets</th>
                                    <th>Matches</th>
                                    <th>Eco</th>
                                    <th>Avg</th>
                                    <th>BBI</th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {leaderboardData.map((row, i) => (
                                <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td>{row.player_name}</td>
                                    <td>{row.team_name}</td>
                                    {statType === 'Batters' ? (
                                        <>
                                            <td>{row.runs}</td>
                                            <td>{row.matches}</td>
                                            <td>{row.hundreds}</td>
                                            <td>{row.fifties}</td>
                                            <td>{row.average}</td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{row.wickets}</td>
                                            <td>{row.matches}</td>
                                            <td>{row.economy}</td>
                                            <td>{row.average}</td>
                                            <td>{row.best_bowling}</td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Leaderboard;
